import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {TranslateService} from '@ngx-translate/core';
import {AlertService} from '../../../services/general/alert.service';
import {SurveyService} from '../../../services/surveys.service';
import {ToastService} from '../../../services/general/toast.service';
import {ModalEmailResponsesComponent} from '../../../modals/modal-email-responses/modal-email-responses.component';
import {MatDialog} from '@angular/material/dialog';
import {HelperService} from '../../../services/general/helper.service';
import {EChartsOption} from 'echarts';
// Ho aggiunto alcune icone Material per coerenza col nuovo design, mantenendo le FA necessarie
import {faBullhorn, faEnvelope, faEye, faTimes, faPlusCircle, faDownload, faTrash, faEdit, faLink, faSearch} from '@fortawesome/free-solid-svg-icons';
import {
  ModalViewSentSurveyQuestionnaireComponent
} from "../../../modals/modal-view-sent-survey-questionnaire/modal-view-sent-survey-questionnaire.component";

interface TeamMember {
  id: number | string;
  email: string;
  name: string;
  response_status: string;
  last_status_invitation_email: string;
  invitation_link: string;
}

@Component({
  selector: 'app-survey-team',
  templateUrl: './survey-team.component.html',
  styleUrls: ['./survey-team.component.scss']
})
export class SurveyTeamComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() members: TeamMember[] = [];
  @Input() surveyId: number | string | null = null;
  @Input() surveyIsClosed: boolean = false;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<TeamMember>([]);
  selectedEmailIds = new Set<number | string>();

  displayedColumns = ['select', 'member_info', 'response_status', 'email_status', 'actions'];

  singleTeamMemberToAddActive = false;
  loading = false;
  emailStatuses = [];

  answeredCount = 0;
  draftCount = 0;
  notAnsweredCount = 0;

  filters = {
    responseStatus: 'ALL',
    emailStatus: [],
    searchValue: '',
  };

  chartOptions!: EChartsOption;

  readonly faEnvelope = faEnvelope;
  readonly faBullhorn = faBullhorn;
  readonly faPlusCircle = faPlusCircle;
  readonly faTimes = faTimes;
  readonly faDownload = faDownload;
  readonly faTrash = faTrash;
  readonly faEdit = faEdit;
  readonly faLink = faLink;
  readonly faEye = faEye;
  readonly faSearch = faSearch;

  constructor(
    private translateService: TranslateService,
    private alert: AlertService,
    private surveyService: SurveyService,
    private toast: ToastService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    if (this.surveyIsClosed) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'select');
    }
    this.initDataSource();
    this.renderChart();
    this.emailStatuses = Array.from(
      new Set(
        (this.members || [])
          .map(m => m.last_status_invitation_email)
          .filter(e => !!e)
      )
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch(property) {
        case 'member_info': return item.name;
        default: return item[property];
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.members) {
      this.dataSource.data = this.members || [];
      this.renderChart();
    }
  }

  private initDataSource(): void {
    this.dataSource = new MatTableDataSource<TeamMember>(this.members || []);
    this.dataSource.filterPredicate = this.createFilterPredicate();
  }

  applyFilter() {
    this.dataSource.filter = JSON.stringify({
      status: this.filters.responseStatus.toUpperCase() || 'ALL',
      search: this.filters.searchValue,
      emailStatus: this.filters.emailStatus || []
    });

    this.selectedEmailIds.clear();
  }

  toggleAddNewTeamMemberMode(): void {
    this.singleTeamMemberToAddActive = !this.singleTeamMemberToAddActive;
  }

  onAddTeamMember(teamMember: TeamMember): void {
    this.toggleAddNewTeamMemberMode();
    this.members.push(teamMember);
    this.dataSource.data = this.members;
    this.renderChart();
  }

  resendInvitation(list, success_callback = (data: any) => {}): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.decision-table.show-decision-table.alert.resend-invitation'),
      (ok: boolean) => {
        if (!ok) return;
        this.surveyService.resendInvitations(this.surveyId, list, (result) => {
          this.toast.success(this.translateService.instant('components.decision-table.show-decision-table.alert.invitation-sent'));
          success_callback(result);
        });
      }
    );
  }

  copyLink(link: string): void {
    navigator.clipboard.writeText(link);
    this.toast.success(this.translateService.instant('generic.success'), this.translateService.instant('generic.link-copied'));
  }

  deleteTeamMembers(list, success_callback = (data: any) => {}): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert'),
      (ok: boolean) => {
        if (!ok) return;
        this.loading = true;
        this.surveyService.deleteTeamMembers(this.surveyId, list, (result) => {
          this.loading = false;
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert-successfully'));
          this.members = this.members.filter((member) => !list.includes(member.id)); // Fix logica filter
          this.dataSource.data = this.members;
          this.renderChart();
          success_callback(result);
        }, (error: any) => {
          this.loading = false;
        });
      }
    );
  }

  showEmailResponses(row: TeamMember | null): void {
    if (!row.last_status_invitation_email) return;
    this.dialog.open(ModalEmailResponsesComponent, {
      panelClass: 'medium-dialog',
      data: {team_member_id: row.id, origin_id: this.surveyId, origin: 'SURVEY'}
    });
  }

  emailColorByTeamMember(row: TeamMember | null): string {
    if (row.last_status_invitation_email) {
      return HelperService.getColorByEmailEvent(row.last_status_invitation_email);
    }
    return '#e0e0e0';
  }

  downloadSurveyTeamDataCSV(): void {
    this.loading = true;
    const payload = Array.from(this.selectedEmailIds);
    this.surveyService.downloadSurveyTeamData(this.surveyId, payload, () => {
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  isEmailSelected(row: TeamMember | null): boolean {
    return !!row && this.selectedEmailIds.has(row.id);
  }

  resendSelectedInvitations(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) return;
    const ids = Array.from(this.selectedEmailIds);
    this.resendInvitation(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  deleteSelectedMembers(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) return;
    // Rimossa restrizione < 2 per permettere cancellazione libera se desiderata, o mantienila se business logic
    if (this.members?.length < 2) return;
    const ids = Array.from(this.selectedEmailIds);
    this.deleteTeamMembers(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  isAllSelected(): boolean {
    const filtered = this.filteredRows();
    return filtered.length > 0 && filtered.every(row => this.selectedEmailIds.has(row.id));
  }

  isSomeSelected(): boolean {
    const filtered = this.filteredRows();
    const selectedCount = filtered.filter(row => this.selectedEmailIds.has(row.id)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  }

  toggleSelectEmail(row: TeamMember): void {
    const next = new Set(this.selectedEmailIds);
    this.selectedEmailIds.has(row.id) ? next.delete(row.id) : next.add(row.id);
    this.selectedEmailIds = next;
    this.cdr.markForCheck();
  }

  toggleSelectAll(event: { checked: boolean }): void {
    event.checked ? this.selectedEmailIds = new Set(this.filteredRows().map(r => r.id)) : this.selectedEmailIds = new Set();
    this.cdr.markForCheck();
  }

  filteredRows(): TeamMember[] {
    return (this.dataSource && this.dataSource.filteredData) ? this.dataSource.filteredData : this.dataSource.data;
  }

  createFilterPredicate(): (data: TeamMember, filter: string) => boolean {
    return (data: TeamMember, filter: string): boolean => {
      const { status, search, emailStatus } = JSON.parse(filter || '{}');
      const normalizedStatus = (status || 'ALL').toUpperCase();
      const normalizedSearch = (search || '').trim().toLowerCase();
      const selectedEmailStatuses: string[] = emailStatus || [];

      if (normalizedStatus !== 'ALL' && data.response_status !== normalizedStatus) return false;

      if (selectedEmailStatuses.length > 0) {
        const memberEmailStatus = data.last_status_invitation_email;
        if (!memberEmailStatus || !selectedEmailStatuses.includes(memberEmailStatus)) return false;
      }

      if (normalizedSearch) {
        const searchable = [data.name, data.email];
        const match = searchable.filter(v => !!v).some(v => String(v).toLowerCase().includes(normalizedSearch));
        if (!match) return false;
      }
      return true;
    };
  }

  private renderChart(): void {
    const members = this.members || [];

    this.answeredCount = members.filter(m => m.response_status === 'SENT').length;
    this.draftCount = members.filter(m => m.response_status === 'DRAFT').length;
    this.notAnsweredCount = members.length - this.answeredCount - this.draftCount;

    this.chartOptions = {
      tooltip: {trigger: 'item'},
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#666' }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false, position: 'center' },

          data: [
            {
              value: this.answeredCount,
              name: this.translateService.instant('generic.who-has-responded'),
              itemStyle: {color: '#27ae60'}
            },
            {
              value: this.notAnsweredCount,
              name: this.translateService.instant('generic.who-has-not-responded'),
              itemStyle: {color: '#e57373'}
            },
            {
              value: this.draftCount,
              name: this.translateService.instant('generic.answers.draft'),
              itemStyle: {color: '#ffb74d'}
            },
          ]
        }
      ]
    };
  }

  emailResponsesAvailable(row): boolean {
    return row.last_status_invitation_email !== 'not-yet-sent';
  }

  impersonable(member): boolean {
    return ['SENT', 'DRAFT'].includes(member.response_status);
  }

  viewSentQuestionnaire(member): void {
    this.dialog.open(ModalViewSentSurveyQuestionnaireComponent, {
      panelClass: 'fullscreen-dialog',
      data: {
        surveyID: this.surveyId,
        impersonateEmail: member.email,
      },
    });
  }

  get answersSent() {
    return this.dataSource?.data?.filter((m) => m.response_status === 'SENT');
  }
}
